<template>
    <div v-if="submitSuccess" class="view" ref="successScreen">
      <SuccessScreen
        :infrastructure-advice="infrastructureAdvice"
        :content-recs="contentRecs"
        :points="pointsResult"
        @reset="resetForm"
      />
    </div>

    <div v-else class="view submission-form">
      <div class="submit-header">
        <h2 v-underline-primary>{{ $t('report.submit.title') }}</h2>
        <h2 v-if="userName" class="">, {{ userName }} 👋</h2>
      </div>
      <p>{{ $t('report.submit.intro') }}</p>
      <ImageUpload
        v-model:fileSelected="fileSelected"
        @update:fileSelected="onFileSelected"
        ref="imageUpload"
        @labels-updated="updateLabels"
        @detected-labels-updated="updateDetectedLabels"
        @situations-updated="situations = $event"
        @file-selected="handleFileSelected"
        @exif-updated="handleExifUpdated"
      >
        <template #below-image>
        <div class="fade-in labels-below" v-if="fileSelected">
          <h2>{{ $t('report.submit.labelsTitle') }}</h2>
          <p>{{ $t('report.submit.labelsIntro') }}</p>
          <div class="loading-spinner" v-if="isLoading">
            <i class="fas fa-bicycle fa-spin"></i>
            <span> {{ $t('report.submit.detectingLabels') }}</span>
          </div>

          <LabelsWrapper
            :labels="allLabels"
            @labels-updated="updateLabels"
            ref="labelsWrapper"
          />
        </div>
        </template>
      </ImageUpload>
      <p class="ai-notice" role="note"><i class="fas fa-robot" aria-hidden="true"></i> {{ $t('report.submit.aiNotice') }}</p>
      <ExplainerScene v-if="!fileSelected" class="explainer" />
      <p class="fineprint" v-if="!fileSelected">
        <i18n-t keypath="report.submit.fineprint" tag="span">
          <template #terms><a :href="$lp('/about') + '#terms'">{{ $t('report.submit.terms') }}</a></template>
          <template #privacy><a :href="$lp('/about') + '#data-protection'">{{ $t('report.submit.privacy') }}</a></template>
        </i18n-t>
      </p>

      <div class="fade-in" v-if="fileSelected">
        <h2>{{ $t('report.submit.severityTitle') }}</h2>
        <p>{{ $t('report.submit.severityQuestion') }}</p>
        <SeverityScale v-model="selectedSeverity" :generated="situations" :labels="allLabels || []" ref="severityScale" @details="ratingDetails = $event" />
      </div>
      <DetailMeter
        v-if="selectedSeverity != null && $pb.authStore.isValid"
        class="fade-in"
        :rating="ratingDetails"
        :labels="allLabels"
        :comment="comment"
        :ideas="infrastructureAdvice ? infrastructureAdvice.length : 0"
      />

      <div v-if="selectedSeverity != null" class="comment fade-in">
        <div class="comment-message">
          <p>{{ $t('report.submit.commentIntro') }}</p>
        </div>
        <textarea v-model="comment" :aria-label="$t('report.submit.commentAria')" :placeholder="$t('report.submit.commentPlaceholder')"></textarea>
        <div class="char-counter">
          <span>{{ comment.length }}</span
          >/500
        </div>
      </div>

      <button
        v-if="selectedSeverity != null"
        class="btn-cta"
        id="seekAdvice"
        @click="getAdvice"
        :disabled="isLoading || comment.length < this.commentBar"
      >
        <span v-if="!isLoading">{{ $t('report.submit.seekAdvice') }}</span>
        <span v-else-if="adviceArrayComplete"> {{ $t('report.submit.getMoreAdvice') }}</span>
        <span v-else
          ><i class="fas fa-bicycle fa-spin"></i> {{ $t('report.submit.askingAdvice') }}</span
        >
      </button>
      <AdviceSection
        ref="adviceSection"
        v-if="infrastructureAdvice && infrastructureAdvice.length"
        :h2="$t('report.submit.adviceTitle')"
        :subtitle="$t('report.submit.adviceSubtitle')"
        :result="infrastructureAdvice"
        :ratings="ratings"
        dismissable
        @update:result="onAdviceResult"
        @update-rating="updateRating"
        @select-sticky-note="selectedStickyNote = $event"
      />
      <button
        v-if="selectedSeverity != null && !adviceArrayComplete && !isLoading"
        type="button"
        class="btn-standard"
        id="skipAdvice"
        @click="skipAdvice"
      >
        {{ $t('report.submit.skipAdvice') }}
      </button>
      <div v-if="adviceArrayComplete && infrastructureAdvice && infrastructureAdvice.length" class="comment">
        <div class="comment-message">
          <p>{{ $t('report.submit.reflectionIntro') }}</p>
        </div>
        <textarea
          v-model="adviceComment"
          :aria-label="$t('report.submit.reflectionAria')"
          :placeholder="$t('report.submit.commentPlaceholder')"
        ></textarea>
      </div>
      <p v-if="submitError" class="submit-error" role="alert">{{ submitError }}</p>
      <button
        v-if="adviceArrayComplete"
        id="submitReport"
        class="btn-cta"
        @click="submitReport"
        :disabled="isLoading"
      >
        <span v-if="!isLoading">{{ $t('report.submit.submitReport') }}</span>
        <span v-else>
          <i class="fas fa-spinner fa-spin"></i> {{ $t('report.submit.submitting') }}
        </span>
      </button>
    </div>
</template>

<!-- InfrastructureGPT is a platform that allows cycling citizens to upload nuisances and assisted by AI propose improvements to the situation. The platform is based on Vue3, YOLOV8, a generative AI model and a custom backend based on Flask / Python  -->

<script>
import axios from "axios";
import ImageUpload from "@/components/ImageUpload.vue";
import ExplainerScene from '@/components/scenes/ExplainerScene.vue';
import AdviceSection from "@/components/AdviceSection.vue";
import LabelsWrapper from "@/components/LabelsWrapper.vue";
import SeverityScale from "@/components/SeverityScale.vue";
import DetailMeter from "@/components/DetailMeter.vue";
import SuccessScreen from "@/components/SuccessScreen.vue";
import { errorHandler } from "@/errorHandler.js";
// import { ref } from 'vue';

export default {
  name: "SubmissionForm",
  components: {
    ExplainerScene,
    ImageUpload,
    AdviceSection,
    SuccessScreen,
    SeverityScale,
    DetailMeter,
    LabelsWrapper,
  },

  data() {
    return {
      file: null,
      exif: {},
      userName: null,
      userId: null,
      submitSuccess: false,
      comment: "",
      adviceComment: "",
      error: null,
      allLabels: null,
      detectedLabels: null,
      isLoading: false,
      infrastructureAdvice: null,
      streamCards: null,
      ratingDetails: null,
      situations: [],
      pointsResult: null,
      ideasMode: null,
      contentRecs: null,
      adviceArray: null,
      adviceArrayComplete: null,
      selectedSeverity: null,
      modelUsed: null,
      submitError: "",
      fileSelected: false,
      // useMockData: true,
      mockDataDelay: 500,
      commentBar: 30,
      mockResult: [
        {
          category: "budget friendly",
          advice:
            "Consider using cost-effective materials and designing the infrastructure in a way that minimizes maintenance and repair costs.",
          record_id: "zhg17si53v8af4d",
        },
        {
          category: "fast to achieve",
          advice:
            "Implement bike lanes and pedestrian crossings at strategic locations to improve safety and accessibility, and paint crosswalks and bike lanes in bright colors to increase visibility.",
          record_id: "4dr1z3f4k1tharl",
        },
        {
          category: "best practice",
          advice:
            "Look into successful infrastructure projects in similar cities, and learn from their approach and strategies to improve your own.",
          record_id: "r3bnx3nniua2hrl",
        },
        {
          category: "super creative",
          advice:
            "Create a community bike-sharing program that incentivizes people to use bikes as their primary mode of transportation, and install solar-powered charging stations for electric bikes.",
          record_id: "bd4mk6z4pywrlf4",
        },
      ],
      // BEGIN advicePrompts Iteration 3
      // advicePrompts: [
      //   // { category: 'Budget-friendly', prompt: 'How can the given situation be improved without exceeding a limited budget?' },
      //   { category: 'Fast to achieve', prompt: 'What would be quick wins with low financial, material effort to temporarily fix the given situation?' },
      //   { category: 'Best practice', prompt: 'What would be optimal best practice long-term solution for this situation? - are there international examples?' },
      //   { category: 'Super creative', prompt: 'What would be a super creative "moonshot" way to improve the given situation? Can be super crazy like thinking on how to revert the bad experience into a very good, social interactive one?.' },
      //   { category: 'Citizen action', prompt: 'What is the next step for me as a citizen to improve the given situation?' },
      //   { category: 'Municipality action', prompt: 'What are the next steps for a mobility officer from the municipality to improve the given situation?' },
      // ],
      // END
      // BEGIN advicePrompts Iteration 4
      advicePrompts: [
        // { category: 'Budget-friendly', prompt: 'How can the given situation be improved without exceeding a limited budget?' },
        {
          category: "Fast to achieve",
          prompt:
            "What would be quick wins with low financial, material effort to temporarily fix the given situation?",
        },
        {
          category: "Best practice",
          prompt:
            "Propose the optimal best practice long-term solution for this situation. If applicable, mention specific international examples to get inspired from (not only on country level if possible).",
        },
        {
          category: "Super creative",
          prompt:
            'What would be a super creative "moonshot" way to improve the given situation? How to revert the bad experience into a very good, social interactive one?.',
        },
        {
          category: "Citizen action",
          prompt:
            "As a proactive citizen, what actions can you take that go beyond the ordinary to improve or raise awareness about this situation? Think beyond conventional methods of involvement or protest. If applicable use the location to propose local groups",
        },
        {
          category: "Municipality action",
          prompt:
            "As a chief city planner starting tomorrow, what would be your first steps to tackle this situation? Which other key stakeholders need to be engaged, and how can they be most effectively involved?",
        },
      ],
      // END
      // A praise (severity 0 on the slider) has nothing to fix: the ideas are about keeping it and spreading it
      praisePrompts: [
        { category: "Why it works", prompt: "What makes this a good example? Name the design details and conditions that should be kept and protected so that it stays good." },
        { category: "Spread it", prompt: "How could this good example be brought to more places, in this city and in other parts of the world? What would need to be adapted to different local conditions?" },
        { category: "Best practice", prompt: "Which similar good examples exist internationally, and what can this one add to them? How could it be documented so that other cities can copy it?" },
        { category: "Citizen action", prompt: "As a citizen, how can you help this example spread and stay alive? Think of simple ways to show appreciation, to tell others and to bring it to the attention of people who decide." },
        { category: "Municipality action", prompt: "As a chief city planner, how would you turn this good example into a standard, and roll it out to other streets or districts? Which stakeholders should be involved?" },
      ],
      adviceRequests: null,

      // advicePrompts: [
      //   { category: 'Budget-friendly', prompt: 'How can the infrastructure be improved without exceeding a limited budget?' },
      //   { category: 'Fast to achieve', prompt: 'What infrastructure changes can be implemented quickly and efficiently?' },
      // ],
    };
  },
  mounted() {
    this.addMutationObserver();
    if (this.$pb.authStore?.model?.username || "") {
      this.userName = this.$pb.authStore?.model?.username;
      this.userId = this.$pb.authStore?.model?.id;
    }
  },
  beforeRouteLeave(to, from, next) {
    this.removeMutationObserver();
    next();
  },

  computed: {
    isPraise() {
      return this.selectedSeverity === 0;
    },
    activePrompts() {
      return this.isPraise ? this.praisePrompts : this.advicePrompts;
    },
    severityText() {
      return ["Praise (a good example, nothing needs to be fixed)", "Nuisance", "Danger"][this.selectedSeverity] ?? this.selectedSeverity;
    },
    fileUrl() {
      if (this.file) {
        return URL.createObjectURL(this.file);
      }
      return null;
    },
  },
  methods: {
    onFileSelected(value) {
      this.fileSelected = value;
      this.isLoading = true;
      // handle file selection event...
    },

    handleExifUpdated(exif) {
      this.exif = exif;
    },
    handleFileSelected(file) {
      this.file = file;
    },

    updateLabels(labels) {
      this.allLabels = labels;
    },
    updateFile(file_name) {
      // this.labeled_file_name = labeled_file_name;
      this.file_name = file_name;
    },
    updateDetectedLabels(labels) {
      this.detectedLabels = labels;
      this.isLoading = false;
    },
    selectStickyNote(index) {
      this.selectedStickyNote = index;
    },

    addMutationObserver() {
      // Watch for changes to the DOM and scroll to the bottom of the page whenever a new element is added
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              window.scrollTo(0, document.body.scrollHeight);
            }
          });
        });
      });

      this.observer.observe(document.body, { childList: true, subtree: true });
    },

    removeMutationObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },

    // People who do not want AI suggestions can submit without them
    skipAdvice() {
      this.infrastructureAdvice = [];
      this.adviceArrayComplete = true;
    },

    // Fallback: one request per idea direction, all at the same time
    async getAdviceParallel() {
        this.infrastructureAdvice = [];
        const slots = new Array(this.activePrompts.length).fill(null);
        const labels = (this.allLabels || []).map((l) => l.label).join(", ");
        await Promise.all(
          this.activePrompts.map(async (advice, index) => {
            try {
              const prompt = `${advice.prompt}\n\nSeverity: ${this.severityText}\n\nUser comment: ${this.comment}\n\nLocation: ${this.exif.country}, ${this.exif.city}\n\nObjects in the scene: ${labels}`;
              const response = await axios.post(
                `${process.env.VUE_APP_SERVER_URL}/get-advice`,
                {
                  prompt,
                  category: advice.category,
                  country: this.exif.country,
                  lang: this.$i18n.locale === "de" ? "German" : "English",
                  lang_code: this.$i18n.locale,
                },
              );
              if (response.data.gpt_info && response.data.gpt_info.model) this.modelUsed = response.data.gpt_info.model;
              slots[index] = {
                record_id: response.data.record_id,
                category: response.data.category,
                advice: response.data.advice,
                advice_en: response.data.advice_en,
                advice_lang: response.data.advice_lang,
              };
              this.infrastructureAdvice = slots.filter(Boolean);
            } catch (error) {
              console.error(error);
              errorHandler(error);
            }
          }),
        );
    },

    // Fires one request for all directions and shows every idea while it is being written (server-sent events).
    // Returns false when nothing could be streamed so the caller can use the fallback.
    async getAdviceStreaming() {
      const labels = (this.allLabels || []).map((l) => l.label).join(", ");
      const situation = `Severity: ${this.severityText}\n\nUser comment: ${this.comment}\n\nLocation: ${this.exif.country}, ${this.exif.city}\n\nObjects in the scene: ${labels}`;
      const cards = {}; // direction index -> card
      const full = {}; // direction index -> complete text received so far
      let shown = {}; // direction index -> characters shown
      let streamDone = false;
      this.infrastructureAdvice = [];
      this.streamCards = cards;

      const publish = () => {
        this.infrastructureAdvice = Object.keys(cards)
          .map(Number)
          .sort((x, y) => x - y)
          .filter((i) => !cards[i].dismissed)
          .map((i) => ({ ...cards[i] }));
      };
      // Reveal the text a few characters at a time so it reads like typing, faster when the backlog grows
      let drained;
      const drainedPromise = new Promise((resolve) => { drained = resolve; });
      const timer = setInterval(() => {
        let pending = false;
        for (const key of Object.keys(cards)) {
          const i = Number(key);
          const text = full[i] || "";
          const done = shown[i] || 0;
          if (done < text.length) {
            const backlog = text.length - done;
            shown[i] = Math.min(text.length, done + Math.max(1, Math.ceil(backlog / 12)));
            cards[i].advice = text.slice(0, shown[i]);
            cards[i].typing = shown[i] < text.length || !cards[i].record_id && !streamDone;
            pending = true;
          } else {
            cards[i].typing = !cards[i].final;
          }
        }
        publish();
        if (streamDone && !pending) {
          clearInterval(timer);
          drained();
        }
      }, 30);

      try {
        const response = await fetch(`${process.env.VUE_APP_SERVER_URL}/get-advice-stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            situation,
            directions: this.activePrompts,
            praise: this.isPraise,
            image_file: this.$refs.imageUpload ? this.$refs.imageUpload.image_file : null,
            country: this.exif.country,
            lang: this.$i18n.locale === "de" ? "German" : "English",
            lang_code: this.$i18n.locale,
          }),
        });
        if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        const handle = (event) => {
          const i = event.index;
          if (event.type === "start") {
            cards[i] = { category: event.category, advice: "", typing: true, streaming: true, _i: i };
            full[i] = "";
            shown[i] = 0;
          } else if (event.type === "delta" && cards[i]) {
            full[i] += event.text;
          } else if (event.type === "card") {
            if (!cards[i]) {
              cards[i] = { category: event.category, advice: "", streaming: true, _i: i };
              shown[i] = 0;
            }
            full[i] = event.advice;
            Object.assign(cards[i], {
              record_id: event.record_id,
              advice_en: event.advice_en,
              advice_lang: event.advice_lang,
              category: event.category,
              final: true,
            });
            if (event.gpt_info && event.gpt_info.model) this.modelUsed = event.gpt_info.model;
          } else if (event.type === "error") {
            console.error(event.message);
          }
        };
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let boundary;
          while ((boundary = buffer.indexOf("\n\n")) !== -1) {
            const chunk = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            if (chunk.startsWith("data:")) {
              try { handle(JSON.parse(chunk.slice(5))); } catch (e) { console.error(e); }
            }
          }
        }
      } catch (error) {
        console.error(error);
        if (!Object.keys(cards).length) {
          clearInterval(timer);
          this.streamCards = null;
          this.infrastructureAdvice = [];
          return false;
        }
      }
      streamDone = true;
      await drainedPromise;
      for (const key of Object.keys(cards)) {
        delete cards[key].streaming;
        delete cards[key].typing;
        delete cards[key].final;
      }
      publish();
      this.streamCards = null;
      if (!Object.keys(cards).length) return false;
      return true;
    },

    // The reader can dismiss ideas while they are still being written: remember which ones, so they do not come back
    onAdviceResult(list) {
      if (this.streamCards) {
        const keep = new Set(list.map((c) => c._i));
        for (const key of Object.keys(this.streamCards)) {
          if (!keep.has(Number(key))) this.streamCards[key].dismissed = true;
        }
      }
      this.infrastructureAdvice = list;
    },

    async getAdvice() {
      this.isLoading = true;
      console.log(this.infrastructureAdvice);
      this.adviceRequests++;

      if (this.useMockData) {
        // Use mock data instead of calling the API
        this.infrastructureAdvice = this.mockResult;
        this.infrastructureAdvice = [];

        for (const advice of this.mockResult) {
          const promise = new Promise((resolve) => {
            setTimeout(() => {
              const adviceObject = {
                category: advice.category,
                advice: advice.advice,
                record_id: advice.record_id,
              };
              resolve(adviceObject);
            }, this.mockDataDelay);
          });

          this.infrastructureAdvice.push(await promise);
        }
      } else {
        // One streamed request writes all ideas; the cards fill up while the text is written.
        // If the stream cannot start, fall back to one request per idea.
        const started = await this.getAdviceStreaming();
        this.ideasMode = started ? "stream" : "parallel";
        if (!started) await this.getAdviceParallel();
      }
      if (this.infrastructureAdvice) {
        console.log(this.infrastructureAdvice);
      }
      this.adviceArrayComplete = true;
      // Handle errors and loading state
      this.isLoading = false;
      this.removeMutationObserver();
      this.observer.disconnect();
    },

    async getContentRecs() {
      try {
        const infrastructureAdvice = this.infrastructureAdvice
          .map((obj) => obj.advice)
          .join(" ");
        const content = `${
          this.comment
        } ${infrastructureAdvice} ${this.allLabels
          .map((l) => l.label)
          .join(", ")}`;
        console.log(content);
        const response = await axios.post(
          `${process.env.VUE_APP_SERVER_URL}/get-content-recs`,
          {
            content: JSON.stringify(content),
          },
        );
        this.contentRecs = response.data;

        return response.data;
      } catch (error) {
        errorHandler(error);
        console.error(error);
        return null;
      }
    },

    async submitReport() {
      try {
        this.isLoading = true;
        this.submitError = "";

        if (!this.exif) {
          const imageUpload = this.$refs.imageUpload.$el;
          imageUpload.scrollIntoView({ behavior: "smooth" });
          const button = document.querySelector(".change-location");
          button.classList.add("flash");
          throw new Error(this.$t("report.submit.errors.adjustLocation"));
        }
        if (!this.$refs.imageUpload.image_file) {
          throw new Error(this.$t("report.submit.errors.noImage"));
        }
        // Call the getContentRecs method to get the content recommendations
        if (!this.contentRecs) {
          this.contentRecs = await this.getContentRecs();
        }
        const now = new Date();
        const title = `Report ${now.toLocaleString()}`;

        // Create report data object with advice and content arrays
        const reportData = {
          image_file: this.$refs.imageUpload.image_file,
          labeled_image_file: this.$refs.imageUpload.labeled_image_file,
          // file: this.file.name,
          exif: JSON.stringify(this.exif), // Convert exif dictionary to JSON string
          title: title,
          user_og: this.userId,
          report_public: this.useMockData ? "False" : "True",
          timestamp: now.toISOString(),
          city: this.exif?.city ?? null,
          country: this.exif?.country ?? null,
          region: this.exif?.region ?? null,
          address: this.exif?.address ?? null,
          geo: `${this.exif.latitude},${this.exif.longitude}`,
          severity: this.selectedSeverity + 1 ?? null,
          comment: this.comment,
          advice_comment: this.adviceComment,
          detected_labels: this.detectedLabels,
          all_labels: this.allLabels,
          advice_array: this.infrastructureAdvice ?? null,
          content_array: this.contentRecs ?? null,
          prompts_used: this.promptsUsed,
          iteration_used: 5,
          model_used: this.useMockData ? "mock" : (this.modelUsed || "gemini"),
          // Free-form details: stays flexible without changing the database
          unstructured_details: {
            v: 1,
            rating: this.ratingDetails,
            ideas: { mode: this.ideasMode, praise: this.isPraise, model: this.modelUsed },
            client: { lang: this.$i18n.locale },
          },
        };

        // If location is not available in exif, prompt the user to select a location using an OSM map picker
        if (!reportData.location) {
          console.log(
            "Location not found in exif. Please select a location on the map.",
          );
        }

        // Send report data to server
        const response = await axios.post(
          `${process.env.VUE_APP_SERVER_URL}/save-report-json`,
          reportData,
        );
        // const response = await axios.post(`${process.env.VUE_APP_SERVER_URL}/save-report`, reportData);

        console.log(response.data);
        console.log(response.data.error);

        // this.isLoading = false;
        this.pointsResult = (response.data && response.data.points) || null;
        if (this.pointsResult && this.$pb.authStore.isValid) this.$pb.collection("users").authRefresh().catch(() => {}); // profile shows the new total
        this.submitSuccess = true;
        this.$emit(
          "submit-success",
          this.infrastructureAdvice,
          this.contentRecs,
        );
      } catch (error) {
        console.error(error);
        errorHandler(error);
        this.submitError = (error.response && error.response.data && error.response.data.error) || error.message || this.$t("report.submit.errors.saveFailed");
      } finally {
        this.isLoading = false;
        // Remove CSS class to show elements again
      }
    },
    resetForm() {
      // Clear form data on successful submission
      // this.result = null;
      // this.file = null;
      // this.allLabels = [];
      // this.comment = '';
      // this.ratings = [];
      // // this.submitSuccess = false;
      // this.file = null;
      // this.comment = '';
      // this.allLabels = [];
      // // this.result = null;
      // this.ratings = [];
      // this.error = null;
      // location.reload();
    },
  },
};
</script>

<style scoped>
.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner {
  border: 16px solid var(--color-background-light);
  border-top: 16px solid var(--color-primary);
  border-radius: 50%;
  width: 120px;
  height: 120px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.flash {
  animation: attention 1s ease-in-out infinite !important;
}

@keyframes attention {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.submit-header {
  display: flex;
  justify-content: center;
}

.submission-form input[type="file"] {
  margin-bottom: 30px;
}

.explainer {
  width: 100%;
  max-width: min(80vw, 420px);
  display: block;
  margin-left: auto;
  margin-right: auto;
}
.submission-form .btn-cta {
  /* primary form action: full width, easy to hit on a phone */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  background-color: var(--color-primary);
  color: var(--color-on-primary);
  font-size: 18px;
  padding: 12px 30px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.submission-form .btn-cta:hover {
  background-color: var(--color-primary-dark);
}

.submission-form label {
  font-weight: bold;
}

.submission-form .form-group {
  margin-bottom: 20px;
}

.submission-form .form-group:last-child {
  margin-bottom: 0;
}

.submission-form .form-group input[type="text"],
.submission-form .form-group .label {
  margin-bottom: 10px;
}

.submission-form .form-group .form-group-header {
  font-weight: bold;
  margin-bottom: 10px;
}

.submission-form .form-group .form-group-header:last-child {
  margin-bottom: 0;
}

.submission-form .form-group .form-group-body {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.submission-form .form-group .form-group-body .form-group-item {
  display: flex;
  align-items: center;
  background-color: var(--color-secondary-light);
  color: var(--color-secondary-dark);
  padding: 8px;
  border-radius: 20px;
}

.submission-form
  .form-group
  .form-group-body
  .form-group-item
  input[type="checkbox"] {
  margin-right: 10px;
}

.submission-form .form-group .form-group-body .form-group-item:last-child {
  margin-bottom: 0;
}

button#seekAdvice,
button#submitAdvice,
button#submitReport {
  margin-bottom: 50px;
}

.char-counter {
  position: relative;
  text-align: end;
  font-size: 14px;
  color: var(--color-secondary);
  margin-top: 5px;
}

.btn-cta 

/* .container.submission-form {
    padding-bottom: 80px;
} */

.dark-mode .file-picker-label {
  border-color: var(--color-border-input);
}

.dark-mode .comment textarea,
.dark-mode div.labels > form > input[type="text"] {
  background-color: var(--color-surface);
  color: var(--color-text);
  border-color: var(--color-border-input);
}
.submit-error { margin: 0; color: var(--color-error-text); font-weight: 600; }
.ai-notice { margin: 0; padding: 12px 16px; border-radius: var(--radius-lg); background: var(--color-subtle); color: var(--color-text); font-size: 13px; line-height: 1.45; text-align: start; }
</style>
